import type { EmployeeEvolution } from '@/api/performance-profile/careerGrowthService';
import type { ProficiencyLevelResponse } from '@/api/performance-profile/performanceProfileService';
import type { SkillReadinessItem } from '@/components/CareerGrowth/skillReadiness';

import { computeSkillReadiness } from '@/components/CareerGrowth/skillReadiness';

/** As many strengths as the section shows side by side. */
export const MAX_STRENGTHS = 3;

/** A competency of the current profile assessed at or above the level it requires. */
export type Strength = SkillReadinessItem & { level: ProficiencyLevelResponse };

const isStrength = (item: SkillReadinessItem): item is Strength =>
    item.level !== null && item.gap === 0;

/**
 * What the employee excels at: the competencies of their current profile they already master,
 * the highest assessed first. A competency never assessed is not a strength, even when the profile
 * asks little of it.
 */
export const selectStrengths = ({
    currentProfile,
    proficiencyLevels,
}: EmployeeEvolution): Strength[] => {
    if (!currentProfile) {
        return [];
    }

    // `sort` is stable: equal levels keep the server's order, priorities first.
    return computeSkillReadiness(currentProfile.skills, proficiencyLevels)
        .items.filter(isStrength)
        .sort((a, b) => b.fraction - a.fraction)
        .slice(0, MAX_STRENGTHS);
};
